<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import PerformanceLevelNotSetBadge from '@/core/badges/PerformanceLevelNotSetBadge.vue';
import PerformanceLevelVisualBadge from '@/core/badges/PerformanceLevelVisualBadge.vue';
import RatingPopover from '@/core/elements/RatingPopover.vue';
import PerformanceLevelRatingOptions from '@/core/performance-visuals/PerformanceLevelRatingOptions.vue';
import { computed, ref, useTemplateRef, watch } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: number | null];
}>();

const props = withDefaults(
  defineProps<{
    accessibleLabel: string;
    disabled?: boolean;
    modelValue: number | null;
    performanceVisual?: Components.Schemas.PerformanceVisualDto | null;
  }>(),
  {
    disabled: false,
    performanceVisual: undefined,
  },
);

const ratingButtonRef = useTemplateRef<HTMLElement>('ratingButtonRef');
const ratingPopoverRef = useTemplateRef<InstanceType<typeof RatingPopover>>('ratingPopoverRef');
const isOpen = ref<boolean>(false);

const performanceLevels = computed<Components.Schemas.PerformanceLevelV2Dto[]>(
  () => props.performanceVisual?.performanceLevelsV2 ?? [],
);

const selectedPerformanceLevel = computed<Components.Schemas.PerformanceLevelV2Dto | null>(() => {
  return performanceLevels.value.find((performanceLevel) => performanceLevel.id === props.modelValue) ?? null;
});

const canAssess = computed<boolean>(() => {
  return !props.disabled && performanceLevels.value.length > 0;
});

const show = async (): Promise<void> => {
  if (!canAssess.value) return;

  await ratingPopoverRef.value?.show();
  isOpen.value = true;
};

const hide = (): void => {
  ratingPopoverRef.value?.hide();
  isOpen.value = false;
};

const toggle = async (): Promise<void> => {
  if (isOpen.value) {
    hide();
    return;
  }

  await show();
};

const handlePerformanceLevelChange = (value: number | null): void => {
  emit('update:modelValue', value);
  hide();
};

watch(canAssess, (value) => {
  if (!value) hide();
});
</script>

<template>
  <button
    ref="ratingButtonRef"
    v-bind="$attrs"
    :class="[
      'appearance-none rounded-sm border-0 bg-transparent p-0 text-inherit outline-none',
      canAssess ? 'cursor-pointer' : 'cursor-default',
      selectedPerformanceLevel ? 'focus:outline-none' : 'outline-inner',
    ]"
    :disabled="!canAssess"
    type="button"
    :aria-label="accessibleLabel"
    aria-haspopup="dialog"
    :aria-expanded="isOpen"
    @click.stop="toggle"
  >
    <PerformanceLevelVisualBadge
      v-if="selectedPerformanceLevel"
      :performance-level="selectedPerformanceLevel"
      selected
      class="size-icon-xl !rounded-sm p-xs text-heading font-semibold text-tint"
    />
    <PerformanceLevelNotSetBadge v-else class="size-icon-xl !rounded-sm p-xs" />
  </button>

  <RatingPopover
    ref="ratingPopoverRef"
    :anchor-element="ratingButtonRef"
    :accessible-label="accessibleLabel"
    @hide="isOpen = false"
  >
    <PerformanceLevelRatingOptions
      appearance="cards"
      :accessible-label="accessibleLabel"
      density="compact"
      :disabled="disabled"
      :model-value="modelValue"
      :performance-visual="performanceVisual"
      @update:model-value="handlePerformanceLevelChange"
    />
  </RatingPopover>
</template>
